/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useEffect } from "react";
import useForm from "react-hook-form";
import { Auth } from "@aws-amplify/auth";
import { FormProps } from "aws-amplify-react/props";
import { Form, Button } from "semantic-ui-react";
import styles from "./LandingPage/LandingForm/LandingForm.module.css";
import cStyles from "../../components/Components.module.css";
import {
  notify,
  usernameFromAuthData,
  codeValidator,
} from "./utils";
import { useHistory } from "react-router-dom";
import { fieldContainer } from "./FieldContainer";
import { useTranslation } from "react-i18next";

type FormData = {
  username: string;
  code: string;
};

const ConfirmSignUp: React.FC<FormProps> = ({
  authData,
  authState = "",
  onStateChange,
  onAuthEvent,
  setLoading,
  loading,
  setMessage,
  hide = [],
}) => {
  const { register, handleSubmit, errors, getValues } = useForm<FormData>();
  const { changeState } = notify(authState, onAuthEvent, onStateChange);
  const history = useHistory();
  const { t } = useTranslation(["confirmSignup", "common"]);

  const defaultMessage = t("received_email");

  useEffect(() => {
    setMessage(defaultMessage);
  }, [defaultMessage, setMessage]);

  const validAuthStates = ["confirmSignUp"];
  if (!validAuthStates.includes(authState)) {
    return null;
  }

  const confirmSignUp = (data: FormData) => {
    const username = usernameFromAuthData(authData) || data.username;
    if (!Auth || typeof Auth.confirmSignUp !== "function") {
      throw new Error(
        "No Auth module found, please ensure @aws-amplify/auth is imported"
      );
    }

    setLoading(true);
    Auth.confirmSignUp(username.toLocaleLowerCase(), data.code)
      .then(() => {
        changeState("signedUp");
        setMessage(t("email_confirmed"));
        history.push("/login");
      })
      .catch((err) => setMessage(err.message || err))
      .finally(() => setLoading(false));
  };

  const resendCode = () => {
    const data = getValues();
    const username = usernameFromAuthData(authData) || data.username;
    if (!Auth || typeof Auth.resendSignUp !== "function") {
      throw new Error(
        "No Auth module found, please ensure @aws-amplify/auth is imported"
      );
    }

    setLoading(true);
    Auth.resendSignUp(username.toLocaleLowerCase())
      .catch((err) => setMessage(err.message || err))
      .finally(() => setLoading(false));
  };

  const username = usernameFromAuthData(authData);

  if (hide && hide.includes(ConfirmSignUp)) {
    return null;
  }

  const ResendCode = () => (
    <div
      className={styles.switchAction}
      style={{ textAlign: "left", marginTop: "2rem" }}
    >
      <span
        id="resendCodeButton"
        className={styles.formLink}
        onClick={() => {
          setMessage(null);
          resendCode();
        }}
      >
        {t("common:resend_code")}
      </span>
    </div>
  );

  return (
    <React.Fragment>
      <Form
        onSubmit={handleSubmit(confirmSignUp)}
        className={styles.signupForm}
      >
        <Form.Group inline className={styles.formGroup}>
          <Form.Field required disabled className={styles.formField}>
            {fieldContainer(
              { label: t("common:email") },
              {
                name: "username",
                defaultValue: username,
                ref: register,
              }
            )}
          </Form.Field>
        </Form.Group>

        <Form.Group inline className={styles.formField}>
          <Form.Field
            required
            error={!!errors.code}
            className={styles.landingFormField}
          >
            {fieldContainer(
              { label: t("common:code"), errors },
              {
                autoFocus: true,
                name: "code",
                placeholder: t("common:enter_code"),
                autoComplete: "off",
                ref: register({
                  required: t("common:code_required") as string,
                  pattern: {
                    value: codeValidator,
                    message: t("common:valid_code"),
                  },
                }),
              }
            )}
          </Form.Field>
        </Form.Group>

        <Button
          className={`${cStyles.lpButton} ${styles.submitFormButton}`}
          id="confirmButton"
          loading={loading}
          disabled={loading}
          onClick={() => {
            setMessage(null);
          }}
        >
          {t("confirm")}
        </Button>

        <ResendCode />
      </Form>
    </React.Fragment>
  );
};

export default ConfirmSignUp;
